/**
 * Input do design system — extraído de ui/index.tsx (limite ~500 linhas/arquivo).
 * Fiação de acessibilidade: com `error`, o input recebe aria-invalid="true" e
 * aria-describedby apontando para o erro; com `hint` (sem erro), para o hint.
 * Só referencia ids de elementos de fato renderizados, e o aria-describedby do
 * chamador é mergeado. Sem dependências do index.tsx — sem ciclo de imports.
 */
import { type InputHTMLAttributes, forwardRef, useId } from 'react'
import { cn } from '../../lib/utils.js'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  /** ids opcionais dos textos de apoio, p/ o input referenciá-los via aria-describedby */
  hintId?: string
  errorId?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      hintId,
      errorId,
      className,
      id,
      // descrição do chamador é MERGEADA com os ids dos textos renderizados
      'aria-describedby': callerDescribedby,
      ...props
    },
    ref,
  ) => {
    const autoId = useId()
    const inputId = id ?? autoId
    const resolvedErrorId = errorId ?? `${inputId}-error`
    const resolvedHintId = hintId ?? `${inputId}-hint`
    // só referencia ids de elementos de fato renderizados: o hint some quando
    // há erro, e o erro só existe com error presente
    const describedBy =
      [
        ...(typeof callerDescribedby === 'string' && callerDescribedby
          ? callerDescribedby.split(/\s+/)
          : []),
        ...(error ? [resolvedErrorId] : []),
        ...(!error && hint ? [resolvedHintId] : []),
      ].join(' ') || undefined
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'w-full rounded-lg border bg-white px-3 py-2 text-sm shadow-sm placeholder-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent',
            error ? 'border-red-400 focus:ring-red-400' : 'border-gray-300',
            className,
          )}
          {...props}
        />
        {hint && !error && (
          <p id={resolvedHintId} className="text-xs text-gray-500">
            {hint}
          </p>
        )}
        {error && (
          <p id={resolvedErrorId} className="text-xs text-red-600">
            {error}
          </p>
        )}
      </div>
    )
  },
)
Input.displayName = 'Input'
